import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonComponent } from '../../../../../shared/components/ui/button/button.component';
import { AuthService } from '../../../../auth-pages/services/auth.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { MeetingCancellationReasonApiService } from '../../../services/meeting-cancellation-reason-api.service';
import {
  MeetingCancellationReasonRequest,
  MeetingCancellationReasonResponse,
} from '../../../models/meeting.model';

@Component({
  selector: 'app-cancellation-reasons-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent],
  templateUrl: './cancellation-reasons-form.component.html',
})
export class CancellationReasonsFormComponent implements OnInit {
  @Input() drawerMode = true;

  @Output() saved = new EventEmitter<MeetingCancellationReasonResponse>();
  @Output() cancelled = new EventEmitter<void>();

  form!: FormGroup;
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  reasonId: number | null = null;
  /** INC 63: el orden lo asigna/gestiona el backend; aquí solo se muestra como informativo al editar. */
  currentSortOrder: number | null = null;
  private originalName = '';

  constructor(
    private fb: FormBuilder,
    private api: MeetingCancellationReasonApiService,
    private authService: AuthService,
    private toast: ToastService
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    // En modo drawer el wrapper invoca reset()/loadReason() al abrir.
  }

  private get businessId(): number {
    return this.authService.getCurrentUser()?.businessId || 1;
  }

  private initForm(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(150)]],
      isActive: [true],
    });
  }

  reset(): void {
    this.isEditing = false;
    this.reasonId = null;
    this.currentSortOrder = null;
    this.error = '';
    this.saving = false;
    this.loading = false;
    this.initForm();
  }

  loadReason(id: number): void {
    this.loading = true;
    this.isEditing = true;
    this.reasonId = id;
    this.api.getById(id, this.businessId).subscribe({
      next: (item) => {
        this.currentSortOrder = item.sortOrder ?? null;
        this.form.patchValue({
          name: item.name,
          isActive: item.isActive ?? true,
        });
        this.originalName = item.name;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar el motivo';
        this.loading = false;
      },
    });
  }

  guardar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    this.error = '';

    const v = this.form.value;
    const body: MeetingCancellationReasonRequest = {
      businessId: this.businessId,
      name: v.name.trim(),
      isActive: v.isActive ?? true,
      // INC 63: en alta se omite el orden para que el backend asigne el siguiente
      // consecutivo; en edición se conserva el orden actual sin modificarlo.
      ...(this.isEditing && this.currentSortOrder !== null
        ? { sortOrder: this.currentSortOrder }
        : {}),
    };

    const req$ = this.isEditing && this.reasonId
      ? this.api.update(this.reasonId, this.businessId, body)
      : this.api.create(body);

    req$.subscribe({
      next: (item) => {
        this.saving = false;
        if (this.isEditing) {
          this.toast.updated('Motivo de cancelación',
            this.originalName !== v.name.trim()
              ? `Se actualizó "${this.originalName}" → "${v.name.trim()}".`
              : `Se actualizó "${v.name.trim()}" correctamente.`);
        } else {
          this.toast.created('Motivo de cancelación', `Se creó "${v.name.trim()}" correctamente.`);
        }
        this.saved.emit(item);
      },
      error: (err) => {
        this.saving = false;
        this.error = err.error?.message || 'Error al guardar';
        this.toast.error({ title: 'Error', message: this.error });
      },
    });
  }

  cancelar(): void {
    this.cancelled.emit();
  }

  hasError(field: string): boolean {
    const c = this.form.get(field);
    return !!c && c.invalid && c.touched;
  }

  getError(field: string): string {
    const c = this.form.get(field);
    if (!c?.errors) return '';
    if (c.errors['required']) return 'Este campo es requerido';
    if (c.errors['maxlength']) return `Máximo ${c.errors['maxlength'].requiredLength} caracteres`;
    return 'Campo inválido';
  }
}
